import React from 'react';
import UpdateLayout from './UpdateLayout';
import { CORTEX_TOOLS_AS_OF, CORTEX_TOOLS_SOURCES, DELIVERY_CONTEXT } from '../../../content/snowflakeUpdates';

export default function CortexToolsPost() {
  return (
    <UpdateLayout
      path="/updates/snowflake-cortex-tools-governed-agents"
      title="Governed AI agents on Snowflake: what Cortex Tools means for your team"
      asOf={CORTEX_TOOLS_AS_OF}
      sources={CORTEX_TOOLS_SOURCES}
    >
      <h2 id="what-it-is" className="pt-2 font-display text-2xl font-semibold tracking-tight text-brand-900">
        What it is
      </h2>
      <p>
        On October 19, 2026, Snowflake announced the preview of Cortex Tools, a part of Cortex AI
        Gateway. It connects the AI clients your organization uses to the business tools your work
        already lives in, using the Model Context Protocol (MCP).
      </p>
      <p>
        Governed MCP servers can be used from Cortex Code (CoCo Desktop and CoCo CLI), Claude, and
        Cursor. The curated catalog includes servers for Atlassian, GitHub, Google Workspace,
        Salesforce, Slack, and Snowflake. A custom MCP server that is not in the catalog can be
        added by URL. Complete any provider-specific setup before enabling a server. In Snowsight,
        the entry point is AI &amp; ML, then AI Gateway, then MCP &amp; Tools.
      </p>

      <h2
        id="why-governance-matters-for-agents"
        className="pt-2 font-display text-2xl font-semibold tracking-tight text-brand-900"
      >
        Why governance matters for agents
      </h2>
      <p>
        An agent that can call business tools needs a boundary. An administrator chooses which MCP
        servers users can reach, and which tools each server exposes. You can allow read-only tools
        and leave write and delete tools unavailable. That is an allowlist per server.
      </p>
      <p>
        Tool-call activity is reviewed in the customer&apos;s Snowflake account with the{' '}
        <code>AGENT_TRACE_TABLE</code> table function, or in the Activity catalog. The allowlist
        and that audit trail are the governance record, and that data stays in the customer&apos;s
        account.
      </p>

      <h2 id="caveats" className="pt-2 font-display text-2xl font-semibold tracking-tight text-brand-900">
        Caveats
      </h2>
      <ul className="list-disc space-y-2 pl-5">
        <li>
          <span className="font-semibold text-ink-900">Preview.</span> Cortex Tools is in preview as
          of the October 19, 2026 release note. Do not plan on it as a generally available feature.
        </li>
        <li>
          <span className="font-semibold text-ink-900">Only ACCOUNTADMIN can administer it.</span>{' '}
          As of that release note, only users with the <code>ACCOUNTADMIN</code> role can administer
          MCP servers in Cortex Tools. Plan role design and least privilege before you enable
          servers. Administration and use are not the same grant: to distribute access, grant the{' '}
          <code>SNOWFLAKE$OPS.MCP$USER</code> database role to each account role that should reach
          an enabled server. Users still complete any required authorization with the third-party
          service.
        </li>
        <li>
          <span className="font-semibold text-ink-900">Test in non-prod.</span> Try the servers and
          the allowlist in a non-prod Snowflake account before you enable them for a production
          role.
        </li>
      </ul>

      <div className="rounded-xl border bg-brand-50 p-6">
        <h2 id="rollout-checklist" className="font-display text-2xl font-semibold tracking-tight text-brand-900">
          Rollout checklist
        </h2>
        <ol className="mt-4 list-decimal space-y-3 pl-5">
        <li>
          <span className="font-semibold text-ink-900">Inventory the agents and tools teams want.</span>{' '}
          List the clients (Cortex Code, Claude, and Cursor) and the MCP servers those teams expect
          to call, including any custom server you would add by URL.
        </li>
        <li>
          <span className="font-semibold text-ink-900">Start with a minimal allowlist.</span> Enable
          one server, and expose only the tools that the team needs. Prefer read-only tools until a
          write is an explicit decision.
        </li>
        <li>
          <span className="font-semibold text-ink-900">Review AGENT_TRACE_TABLE regularly.</span> Use
          the <code>AGENT_TRACE_TABLE</code> table function, or the Activity catalog, to see which
          tools were called.
        </li>
        <li>
          <span className="font-semibold text-ink-900">Manage the configuration as code.</span> Keep
          the server and tool configuration in Terraform, and promote it through CI/CD such as Azure
          DevOps Pipelines or GitHub Actions.
        </li>
        <li>
          <span className="font-semibold text-ink-900">Run in non-prod first.</span> Confirm the
          allowlist and the audit trail in a non-prod Snowflake account before the same
          configuration reaches production.
        </li>
        <li>
          <span className="font-semibold text-ink-900">Align with existing governed semantic views.</span>{' '}
          When an agent also answers from warehouse data, keep those answers on the governed
          semantic views the account already uses, so tools and metrics share the same definitions.
        </li>
        </ol>
      </div>
      <p>
        MyOrgLab builds governed AI and analytics in the customer&apos;s account. {DELIVERY_CONTEXT}
      </p>
    </UpdateLayout>
  );
}
